import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {NavStoreSource} from 'constants/SourceConstants';
import {Tag} from 'folder-items/FileConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {restToGraphQL} from 'graphql-client/declarative-translations/external/translate';
import NavStore from 'nav/NavStore';
import notifications from 'notifications';
import {Party} from 'parties/PartyConstants';
import preferences from 'Preferences';
import EditableRequestAssignees from 'requests/assignees/EditableRequestAssignees';
import prepareAssigneesForServer from 'requests/assignees/prepareAssigneesForServer';
import {CreateRequestArgs} from 'requests/inputs/InputReduxActions';
import EditableRequestSampleTable from 'requests/samples-table/EditableRequestSampleTable';
import AddTaskSchemaDropdown from 'requests/tasks/AddTaskSchemaDropdown';
import {makeRequestTaskGraphQL} from 'requests/tasks/MakeRequestTask';
import prepareRequestTasksForServer from 'requests/tasks/prepareRequestTasksForServer';
import prepareTaskSampleGroupAssociationsForServer from 'requests/tasks/prepareTaskSampleGroupAssociationsForServer';
import RequestTaskCard from 'requests/tasks/RequestTaskCard';
import {UIRequestTask} from 'requests/tasks/RequestTaskConstants';
import {mergeTemplateTags} from 'requests/templates/RequestTemplateUtil';
import SchemaTagInputs from 'schemas/tags/SchemaTagInputs';
import * as TagUtil from 'schemas/tags/TagUtil';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import * as Headers from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {UnconnectedProjectSelector} from 'ui-library/ProjectSelector';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import pick from 'util/pick';
import {PreparedUISampleGroup} from 'workflows/samples/PreparedSampleGroupConstants';
import prepareSampleGroupsForServer from 'workflows/samples/prepareSampleGroupsForServer';
import {SampleGroupUpdate} from 'workflows/WorkflowConstants';

import {RequestTemplateGraphQL} from '../../../graphql-fragments/requests/__generated/RequestTemplateGraphQL';
import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import {
  getLegacyRequestObjectName,
  RequestAssignee,
  RequestPreferences,
  RequestSchema,
} from '../RequestConstants';
import {CreateRequestModalQuery} from './__generated/CreateRequestModalQuery';

interface CreateRequestModalBodyProps {
  isLoading: boolean;
  requestor: Party;
  assignees: Array<RequestAssignee>;
  projectId: string | null;
  requestTaskSchemasById: Record<string, RequestTaskSchemaGraphQL>;
  // Tasks may include initial tasks that were stipulated by the server, however these sever tasks should all
  // already be converted to UIRequestTasks.
  requestTasks: Array<UIRequestTask>;
  sampleGroupCidsByRequestTaskCid: {[requestTaskCid: string]: Array<string>};
  sampleGroups: Array<PreparedUISampleGroup>;
  schema: RequestSchema;
  sources: Array<NavStoreSource>;
  tags?: Array<Tag>;
  template: RequestTemplateGraphQL | null;
  onUpdateData: (data: Partial<State>) => void;
  requestTemplatesById: Record<string, RequestTemplateGraphQL>;
}

function _getMatchingSource<T extends {id: string}>(sources: Array<T>, projectId: string): T | undefined {
  return sources.find((s) => s.id === projectId);
}

class CreateRequestModalBody extends React.Component<CreateRequestModalBodyProps> {
  _handleUpdateAssignees = (assignees: Array<RequestAssignee>): void => {
    this.props.onUpdateData({assignees});
  };

  handleSelectProject = (projectId: string): void => {
    const source = _getMatchingSource(this.props.sources, projectId);
    if (!source) {
      notifications.error(`Invalid projectId: ${projectId}`);
    } else if (source.permissions.canCreateRequest) {
      this.props.onUpdateData({projectId});
    } else {
      notifications.error(`${getLegacyRequestObjectName()} creation permission required for ${source.name}`);
    }
  };

  _handleUpdateSampleGroups = (sampleGroups: Array<PreparedUISampleGroup>): void => {
    this.props.onUpdateData({sampleGroups});
  };

  _handleUpdateSampleGroupIdsByRequestTaskId = (sampleGroupCidsByRequestTaskCid: {
    [requestTaskCid: string]: Array<string>;
  }): void => {
    this.props.onUpdateData({sampleGroupCidsByRequestTaskCid});
  };

  _handleOnCancelEdit = (): void => {};

  _handleOnEdit = (): void => {};

  _handleOnSave = (): void => {};

  _handleUpdateTags = (tags: Array<Tag>): void => {
    this.props.onUpdateData({tags});
  };

  _handleAddTaskOfSchema = (requestTaskSchema: RequestTaskSchemaGraphQL): void => {
    const requestTask = makeRequestTaskGraphQL(requestTaskSchema);
    const taskTemplate =
      this.props.template && this.props.template.taskTemplates
        ? this.props.template.taskTemplates.find((t) => t.schema.id === requestTaskSchema.id)
        : null;
    if (taskTemplate) {
      requestTask.tags = mergeTemplateTags(requestTask.tags, taskTemplate.tagsJson);
    }
    this.props.onUpdateData({
      requestTasks: [...this.props.requestTasks, requestTask],
      sampleGroupCidsByRequestTaskCid: {...this.props.sampleGroupCidsByRequestTaskCid, [requestTask.cid]: []},
    });
  };

  _handleRemoveRequestTask = (taskCid: string): void => {
    this.props.onUpdateData({
      requestTasks: this.props.requestTasks.filter((requestTask) => requestTask.cid !== taskCid),
      // eslint-disable-next-line no-restricted-syntax
      sampleGroupCidsByRequestTaskCid: _.omit(this.props.sampleGroupCidsByRequestTaskCid, taskCid),
    });
  };

  _handleUpdateRequestTask = (newTask: UIRequestTask): void => {
    // eslint-disable-next-line no-restricted-syntax
    const taskIndex = _.findIndex(this.props.requestTasks, {cid: newTask.cid});
    this.props.onUpdateData({
      requestTasks: reactUpdate(this.props.requestTasks, {[taskIndex]: {$set: newTask}}),
    });
  };

  override render(): JSX.Element {
    if (
      this.props.isLoading ||
      this.props.requestTaskSchemasById == null ||
      this.props.requestTemplatesById == null
    ) {
      return <CenteredHorizontalThrobber />;
    }

    const editableRequestAssignees = (
      <Col xs={6}>
        <FieldLabel label="Assigned to" labelStyle={FieldLabel.STYLES.LABEL} />
        <EditableRequestAssignees
          assignees={this.props.assignees}
          onUpdateAssignees={this._handleUpdateAssignees}
          organizationId={this.props.schema.organization.id}
          shouldChangeBeConfirmed={false}
          useLegacyEntityInput={true}
        />
      </Col>
    );

    const editableRequestor = (
      <Col xs={6}>
        <FieldLabel label="Requestor" labelStyle={FieldLabel.STYLES.LABEL} />
        <EntityInputWithSearch
          allowOrganizations={false}
          allowTeams={false}
          onChange={(entities: Array<Party>) => {
            const [entity] = entities;
            this.props.onUpdateData({requestor: entity});
          }}
          shouldAllowMultiple={false}
          size={TagsInputSize.SMALL}
          value={
            this.props.requestor ? [{id: this.props.requestor.id, handle: this.props.requestor.handle}] : []
          }
          placeholder={`Will default to the ${getLegacyRequestObjectName({lowerCase: true})} creator`}
        />
      </Col>
    );

    return (
      <div>
        <Row>
          <Col xs={6}>
            <FieldLabel isRequired={true} label="Project" labelStyle={FieldLabel.STYLES.LABEL} />
            <UnconnectedProjectSelector
              onProjectSelected={this.handleSelectProject}
              sources={this.props.sources}
              selectedProjectId={this.props.projectId}
              level="CREATE_REQUEST"
              enableRedesign={true}
            />
          </Col>
          {BenchlingConfig.REQUESTS_ENABLE_REQUESTOR ? editableRequestor : editableRequestAssignees}
        </Row>
        {BenchlingConfig.REQUESTS_ENABLE_REQUESTOR && <Row>{editableRequestAssignees}</Row>}
        <div className="u-spacing-top">
          <SchemaTagInputs
            itemLinkAllowed={true}
            isCreating={true}
            onUpdateTags={this._handleUpdateTags}
            tags={this.props.tags}
          />
        </div>
        {Object.values(this.props.requestTaskSchemasById).length > 0 && (
          <div className="u-spacing-top">
            <Headers.Title>
              Tasks
              <AddTaskSchemaDropdown
                requestTaskSchemasById={this.props.requestTaskSchemasById}
                requestTaskSchemaIds={this.props.schema.requestTaskSchemaIds}
                onSelectSchema={this._handleAddTaskOfSchema}
              />
            </Headers.Title>
            {this.props.requestTasks.length > 0 ? (
              this.props.requestTasks.map((requestTask) => (
                <div key={requestTask.cid} className="u-tiny-spacing-top">
                  <RequestTaskCard
                    isCreating={true}
                    isWritable={true}
                    requestTask={requestTask}
                    requestTaskSchema={this.props.requestTaskSchemasById[requestTask.schemaId]}
                    onRemove={(): void => {
                      this._handleRemoveRequestTask(requestTask.cid);
                    }}
                    onUpdate={this._handleUpdateRequestTask}
                  />
                </div>
              ))
            ) : (
              <p className="u-faded">No tasks added</p>
            )}
          </div>
        )}
        {this.props.schema.sampleGroupSchema && (
          <div className="u-spacing-top">
            <EditableRequestSampleTable
              canToggleEditMode={false}
              isWritable={true}
              isEditing={true}
              isSaving={false}
              requestTaskSchemasById={this.props.requestTaskSchemasById}
              requestTasks={this.props.requestTasks}
              sampleGroupIdsByRequestTaskId={this.props.sampleGroupCidsByRequestTaskCid}
              sampleGroupSchema={restToGraphQL('SampleGroupSchema', this.props.schema.sampleGroupSchema)}
              sampleGroups={this.props.sampleGroups}
              fulfillableStatuses={{}}
              onUpdateSampleGroups={this._handleUpdateSampleGroups}
              onUpdateSampleGroupIdsByRequestTaskId={this._handleUpdateSampleGroupIdsByRequestTaskId}
              // This sample table is never non-editable or saved.
              onCancelEdit={this._handleOnCancelEdit}
              onEdit={this._handleOnEdit}
              onSave={this._handleOnSave}
            />
          </div>
        )}
      </div>
    );
  }
}

interface State {
  projectId: string | null;
  requestor: Party;
  assignees: Array<RequestAssignee>;
  tags: Array<Tag>;
  // Tasks may include initial tasks that were stipulated by the server, however these sever tasks should all
  // already be converted to UIRequestTasks.
  requestTasks: Array<UIRequestTask>;
  sampleGroupCidsByRequestTaskCid: {[requestTaskCid: string]: Array<string>};
  sampleGroups: Array<PreparedUISampleGroup>;
  creationInProcess: boolean;
  isTemplateLoading: boolean;
}

export interface CreateRequestModalBaseProps {
  // Props provided by getInitialRequestProps (for both "new request" and "copy request" operations).
  schema: RequestSchema;
  template: RequestTemplateGraphQL | null;
  inCopyMode?: boolean;
  initialTags: Array<Tag>;
  initialRequestor: Party;
  initialAssignees: Array<RequestAssignee>;
  // Tasks may include initial tasks that were stipulated by the server, however these sever tasks should all
  // already be converted to UIRequestTasks.
  initialRequestTasks: Array<UIRequestTask>;
  initialProjectId?: string | null;
  // Props provided only during a "copy request" operation
  initialSampleGroups?: Array<PreparedUISampleGroup>;
  initialSampleGroupCidsByRequestTaskCid?: {[requestTaskCid: string]: Array<string>};
  // Handlers
  createRequest: (createRequestArgs: CreateRequestArgs) => Promise<void>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

/** @package */
export interface QueryingCreateRequestModalProps extends CreateRequestModalBaseProps {
  // Props provided by NavStore
  areSourcesLoading: boolean;
  sources: Array<NavStoreSource>;
}

/** @package */
export interface CreateRequestModalProps extends QueryingCreateRequestModalProps {
  requestTaskSchemasById: Record<string, RequestTaskSchemaGraphQL> | null;
  requestTemplatesById: Record<string, RequestTemplateGraphQL>;
}

export class CreateRequestModal extends React.Component<CreateRequestModalProps, State> {
  override state: State = {
    projectId: this._getInitialProjectId(),
    requestor: this.props.initialRequestor,
    assignees: this._getInitialAssignees(),
    tags: this._getInitialTags(),
    requestTasks: this.props.initialRequestTasks,
    // Non-sparseness is important when creating a request with request tasks
    sampleGroupCidsByRequestTaskCid:
      this.props.initialSampleGroupCidsByRequestTaskCid ||
      _.object(this.props.initialRequestTasks.map((task) => [task.cid, []])),
    sampleGroups: this.props.initialSampleGroups || [],
    creationInProcess: false,
    isTemplateLoading:
      this.props.schema.requestTemplateIds.length > 0 &&
      this.props.schema.requestTemplateIds.some(
        (templateId) => this.props.requestTemplatesById[templateId] == null
      ),
  };

  override componentDidUpdate(prevProps: CreateRequestModalProps, prevState: State): void {
    if (
      Object.keys(prevProps.requestTemplatesById).length !==
        Object.keys(this.props.requestTemplatesById).length ||
      Object.keys(prevProps.requestTaskSchemasById ?? {}).length !==
        Object.keys(this.props.requestTaskSchemasById ?? {}).length ||
      prevProps.areSourcesLoading !== this.props.areSourcesLoading
    ) {
      const initialProjectId = this._getInitialProjectId();
      if (initialProjectId != null && prevState.projectId !== initialProjectId) {
        // It's okay to update state here because this condition will only update state once.
        this.setState({projectId: initialProjectId});
      }
      const initialAssignees = this._getInitialAssignees();
      if (prevState.assignees !== initialAssignees) {
        this.setState({assignees: initialAssignees});
      }
      const initialTags = this._getInitialTags();
      if (prevState.tags !== initialTags) {
        this.setState({tags: initialTags});
      }
      if (
        this.props.schema.requestTemplateIds.length === 0 ||
        this.props.schema.requestTemplateIds.every(
          (templateId) => this.props.requestTemplatesById[templateId] != null
        )
      ) {
        this.setState({isTemplateLoading: false});
      }
    }
  }

  onUpdateData = (data: Partial<State>): void => {
    this.setState(data as State);
    if (data.projectId) {
      preferences.set(RequestPreferences.LAST_USED_REQUEST_PROJECT, data.projectId);
    }
  };

  _getInitialProjectId(): string | null {
    const initialProjectId = this.props.inCopyMode
      ? this.props.initialProjectId
      : this.props.requestTemplatesById?.[this.props.schema.requestTemplateIds?.[0]]?.defaultProject?.id ||
        this.props.initialProjectId ||
        preferences.get(RequestPreferences.LAST_USED_REQUEST_PROJECT) ||
        null;

    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (initialProjectId === null) {
      return initialProjectId;
    }
    if (this.props.areSourcesLoading) {
      return null;
    }
    const matchedSource = _getMatchingSource(this.props.sources, initialProjectId);
    if (matchedSource && matchedSource.permissions.canCreateRequest) {
      return initialProjectId;
    }
    return null;
  }

  _getInitialAssignees(): Array<RequestAssignee> {
    return this.props.inCopyMode
      ? this.props.initialAssignees
      : (this.props.requestTemplatesById?.[this.props.schema.requestTemplateIds?.[0]]?.assigneesJson ??
          this.props.initialAssignees);
  }

  _getInitialTags(): Array<Tag> {
    return this.props.inCopyMode
      ? this.props.initialTags
      : (this.props.requestTemplatesById?.[this.props.schema.requestTemplateIds?.[0]]?.tagsJson ??
          this.props.initialTags);
  }

  _isCreateButtonDisabled(): boolean {
    return this.state.projectId == null || this.state.creationInProcess;
  }

  _areAllSampleGroupTasksPairedAndNotify(sampleGroups: Array<SampleGroupUpdate>): boolean {
    if (sampleGroups.length === 0) {
      // If there are no samples, no pairing is required.
      return true;
    }
    if (this.state.requestTasks.length === 0) {
      // If there are no tasks, no pairing is required.
      return true;
    }

    // Check that all samples have at least one task, and all tasks are performed on at least one sample.
    // To track samples, create a set of all sampleGroupCids which may not have a task ("untasked").
    const untaskedSampleGroupCids = new Set(sampleGroups.map((sampleGroup) => sampleGroup.cid));
    for (const taskCid of Object.keys(this.state.sampleGroupCidsByRequestTaskCid)) {
      if (this.state.sampleGroupCidsByRequestTaskCid[taskCid].length === 0) {
        notifications.error('All tasks must have at least one sample selected.');
        return false;
      }
      if (untaskedSampleGroupCids.size !== 0) {
        for (const sampleGroupCid of this.state.sampleGroupCidsByRequestTaskCid[taskCid]) {
          // This sample is associated with a task. Remove it from untaskedSampleGroupCids.
          untaskedSampleGroupCids.delete(sampleGroupCid);
        }
      }
    }
    // Check if any samples still do not have tasks.
    if (untaskedSampleGroupCids.size !== 0) {
      notifications.error('All samples must have at least one task selected.');
      return false;
    }
    return true;
  }

  _handleCreateRequest = async (): Promise<void> => {
    this.setState({creationInProcess: true});
    const sampleGroupsForServer = prepareSampleGroupsForServer(this.state.sampleGroups);
    if (!this._areAllSampleGroupTasksPairedAndNotify(sampleGroupsForServer)) {
      this.setState({creationInProcess: false});
      return;
    }
    if (!TagUtil.areTagsValidAndNotify(this.state.tags)) {
      this.setState({creationInProcess: false});
      return;
    }

    const tagsToSet = TagUtil.getTagsToSet(this.state.tags);
    const createRequestArgs: CreateRequestArgs = {
      assignees: prepareAssigneesForServer(this.state.assignees),
      projectId: this.state.projectId,
      requestorId: this.state.requestor?.id || this.props.initialRequestor.id,
      requestTasks: prepareRequestTasksForServer(this.state.requestTasks),
      sampleGroupIdsByRequestTaskId: prepareTaskSampleGroupAssociationsForServer({
        requestTasks: this.state.requestTasks,
        sampleGroupIdsByRequestTaskId: this.state.sampleGroupCidsByRequestTaskCid,
        sampleGroupsForServer,
      }),
      sampleGroups: sampleGroupsForServer,
      schemaId: this.props.schema.id,
      tags: tagsToSet,
    };

    try {
      await this.props.createRequest(createRequestArgs);
      this.props.onRequestSubmit();
    } catch (err) {
      // A toast has already been shown.
      this.setState({creationInProcess: false});
      throw err;
    }
  };

  override render(): JSX.Element {
    return (
      <Modal
        large={true}
        closeableOnEscapeKey={false}
        scrollable={true}
        expandHeight={true}
        title={`Create ${this.props.schema.name} ${getLegacyRequestObjectName({lowerCase: true})}`}
        buttons={[
          {
            text: this.state.creationInProcess ? 'Creating...' : 'Create',
            onClick: this._handleCreateRequest,
            style: ButtonStyle.PRIMARY,
            disabled: this._isCreateButtonDisabled(),
            dataTestAttrs: {
              component: 'CreateRequestModal',
              element: 'modal',
              key: ModalButtonKey.CREATE,
            },
          },
        ]}
        onRequestClose={this.props.onRequestClose}
      >
        <CreateRequestModalBody
          // Externally provided (Redux, etc)
          isLoading={this.props.areSourcesLoading || this.state.isTemplateLoading}
          sources={this.props.sources}
          requestTaskSchemasById={this.props.requestTaskSchemasById!}
          schema={this.props.schema}
          onUpdateData={this.onUpdateData}
          // Internally-managed in state
          projectId={this.state.projectId}
          requestor={this.state.requestor}
          assignees={this.state.assignees}
          tags={this.state.tags}
          requestTasks={this.state.requestTasks}
          sampleGroupCidsByRequestTaskCid={this.state.sampleGroupCidsByRequestTaskCid}
          sampleGroups={this.state.sampleGroups}
          template={this.props.template}
          requestTemplatesById={this.props.requestTemplatesById}
        />
      </Modal>
    );
  }
}

export const CREATE_REQUEST_MODAL_QUERY: DocumentNode = gql`
  query CreateRequestModalQuery @skipAudit @engComponent(name: REQUESTS) {
    ...AllRequestTaskSchemasGraphQL
    ...AllRequestTemplatesGraphQL
  }
  ${getFragmentWrapper('AllRequestTaskSchemas').fragment}
  ${getFragmentWrapper('AllRequestTemplates').fragment}
`;

function QueryingCreateRequestModal(props: QueryingCreateRequestModalProps): JSX.Element | null {
  const result = useBenchlingQuery<CreateRequestModalQuery>(CREATE_REQUEST_MODAL_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  if (result.type === QueryResultType.SUCCESS) {
    const allRequestTaskSchemasById: Record<string, RequestTaskSchemaGraphQL> = _.indexBy(
      result.data.requestTaskSchemas,
      'id'
    );

    const requestTemplatesById: Record<string, RequestTemplateGraphQL> = _.indexBy(
      result.data.requestTemplates,
      'id'
    );

    const requestTaskSchemasById = props.schema.requestTaskSchemaIds.some(
      (id) => allRequestTaskSchemasById[id] == null
    )
      ? null
      : pick(allRequestTaskSchemasById, ...props.schema.requestTaskSchemaIds);

    return (
      <CreateRequestModal
        {...props}
        requestTaskSchemasById={requestTaskSchemasById}
        requestTemplatesById={requestTemplatesById}
      />
    );
  } else {
    return (
      <Modal
        large={true}
        title={`Create ${props.schema.name} ${getLegacyRequestObjectName({lowerCase: true})}`}
        buttons={[{text: 'Create', disabled: true}]}
        onRequestClose={props.onRequestClose}
      >
        <CenteredHorizontalThrobber />
      </Modal>
    );
  }
}

export default NavStore.connect((navStore: typeof NavStore) => ({
  areSourcesLoading: navStore.isLoading(),
  sources: navStore.getSources(),
}))(QueryingCreateRequestModal) as React.ComponentType<CreateRequestModalBaseProps>;
